import React, { useState, useEffect, useCallback, useRef } from 'react';
import CharacterList from '../../components/CharacterList';
import CharacterForm from '../../components/CharacterForm';
import { Card, Button } from '../../components/ui';
import { useCharacters, useCharacterList, useCharacterActions } from '../../contexts/CharacterContext';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../services/utils';
import WelcomeBackBanner from '../../components/WelcomeBackBanner/WelcomeBackBanner';
import './CharacterManager.css';

/**
 * CharacterManager container component
 * Manages character selection, creation, and editing
 *
 * @param {Object} props - Component props
 * @param {Array} props.selectedCharacters - Currently selected characters
 * @param {function} props.onCharacterSelect - Callback when character selection changes
 */
const CharacterManager = ({ selectedCharacters = [], onCharacterSelect }) => {
  // Use hooks from CharacterContext instead of local state
  const { characters, isLoading, error: contextError } = useCharacterList();
  const { fetchCharacters, createCharacter, updateCharacter } = useCharacterActions();
  
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [localError, setLocalError] = useState(null);
  const formRef = useRef(null);
  
  // Combine context error with local error
  const error = contextError || localError;
  
  // Get auth context values including anonymous activity tracking
  const { activeChildProfile, isAuthenticated, trackCharacterCreation } = useAuth();

  // Fetch characters on component mount
  useEffect(() => {
    logger.info('CharacterManager', 'Fetching characters via CharacterContext');
    fetchCharacters();
  }, []); // Only run once on mount

  // Handle character creation
  const handleCreateCharacter = useCallback(async (name, creature, description, pronouns) => {
    try {
      if (!activeChildProfile) {
        setLocalError('Please select a child profile before creating characters.');
        return;
      }
      
      logger.info('CharacterManager', 'Creating character via CharacterContext');
      // Use createCharacter from context with additional profile info
      const newCharacter = await createCharacter({
        name,
        creature,
        description,
        pronouns,
        profile_id: activeChildProfile.id
      });
      
      logger.debug('CharacterManager', 'Character created successfully', newCharacter);
      
      // Auto-select the new character
      if (onCharacterSelect && selectedCharacters.length < 3) {
        onCharacterSelect(newCharacter, true);
      }
      
      // Track character creation for anonymous users
      if (!isAuthenticated) {
        trackCharacterCreation();
      }
      
      setIsCreating(false);
      setLocalError(null);
      
      if (formRef.current) {
        formRef.current.reset();
      }
    } catch (err) {
      logger.error('CharacterManager', 'Failed to create character:', err);
      
      // Check for specific error message about child profile
      const errorMessage = err.response?.data?.error || 'Failed to create character. Please try again.';
      
      // If there's debug info, log it for troubleshooting
      if (err.response?.data?.debug_info) {
        logger.error('CharacterManager', 'Debug info:', err.response.data.debug_info);
      }
      
      // If this is the child profile error, provide more specific guidance
      if (errorMessage.includes('child profile')) {
        setLocalError(`${errorMessage} Please ensure you have created a child profile and it is selected.`);
      } else {
        setLocalError(errorMessage);
      }
      
      // Re-throw so the form can reset its submitting state
      throw err;
    }
  }, [createCharacter, onCharacterSelect, selectedCharacters.length, activeChildProfile, isAuthenticated, trackCharacterCreation]);

  // Handle character update
  const handleUpdateCharacter = useCallback(async (name, creature, description, pronouns) => {
    if (!editingCharacter) return;
    
    try {
      logger.info('CharacterManager', `Updating character ${editingCharacter.id} via CharacterContext`);
      
      // Use updateCharacter from context
      const updatedCharacter = await updateCharacter(editingCharacter.id, {
        name, 
        creature,
        description,
        pronouns
      });
      
      logger.debug('CharacterManager', 'Character updated successfully', updatedCharacter);
      
      // Update in selected characters if it's selected
      const isSelected = selectedCharacters.some(c => c.id === updatedCharacter.id);
      if (isSelected && onCharacterSelect) {
        onCharacterSelect(updatedCharacter, false); // Remove old version
        onCharacterSelect(updatedCharacter, true);  // Add updated version
      }
      
      setEditingCharacter(null);
      setLocalError(null);
    } catch (err) {
      logger.error('CharacterManager', `Failed to update character ${editingCharacter.id}:`, err);
      setLocalError('Failed to update character. Please try again.');
      
      // Re-throw so the form can reset its submitting state
      throw err;
    }
  }, [editingCharacter, updateCharacter, onCharacterSelect, selectedCharacters]);

  // Start creating a new character
  const handleStartCreate = useCallback(() => {
    // Check if the form has unsaved changes
    if (editingCharacter && formRef.current?.hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to start creating a new character?')) {
        return;
      }
    }
    
    setEditingCharacter(null);
    setIsCreating(true);
  }, [editingCharacter]);

  // Start editing an existing character
  const handleStartEdit = useCallback((character) => {
    // Check if the form has unsaved changes
    if ((editingCharacter || isCreating) && formRef.current?.hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to edit a different character?')) {
        return;
      }
    }
    
    setEditingCharacter(character);
    setIsCreating(false);
  }, [editingCharacter, isCreating]);

  // Cancel editing/creating
  const handleCancel = useCallback(() => {
    setEditingCharacter(null);
    setIsCreating(false);
    
    if (formRef.current) {
      formRef.current.reset();
    }
  }, []);

  // We don't need this function anymore, as we're getting the selected IDs directly
  // and using them in CharacterList

  // Handle selection change from list
  const handleSelectionChange = useCallback((character, selected) => {
    if (onCharacterSelect) {
      onCharacterSelect(character, selected);
    }
  }, [onCharacterSelect]);

  return (
    <div className="character-manager">
      <WelcomeBackBanner />
      
      {error && (
        <div className="character-manager__error">{error}</div>
      )}
      
      <div className="character-manager__content">
        <div className="character-manager__list-section">
          <Card>
            <h3 className="character-manager__list-title">Available</h3>
            
            <CharacterList 
              characters={characters}
              selectedCharacterIds={selectedCharacters.map(c => c.id)}
              onSelectionChange={handleSelectionChange}
              loading={isLoading}
              onEditCharacter={handleStartEdit}
            />
            
            {/* "New Character" button removed as requested */}
          </Card>
        </div>
        
        <div className="character-manager__form-section">
          <Card>
            <h3 className="character-manager__form-title">
              {isCreating 
                ? 'Create New Character' 
                : editingCharacter 
                  ? `Edit ${editingCharacter.name}` 
                  : 'Details'}
            </h3>
            {/* Profile indicator removed */}
            
            {(isCreating || editingCharacter) ? (
              <CharacterForm
                ref={formRef}
                character={editingCharacter}
                onSubmit={editingCharacter ? handleUpdateCharacter : handleCreateCharacter}
                onCancel={handleCancel}
                isEditing={!!editingCharacter}
              />
            ) : (
              <div className="character-manager__no-selection">
                <p>Select to edit, or create a new one</p>
                <Button 
                  variant="primary" 
                  onClick={handleStartCreate}
                >
                  New Character
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
      
      <div className="character-manager__selection">
        <h3 className="character-manager__selection-title">
          Selected
        </h3>
        
        <div className="character-manager__selected-list">
          {selectedCharacters.length === 0 ? (
            <p className="character-manager__selection-empty">
              None yet.
            </p>
          ) : (
            selectedCharacters.map(character => (
              <div 
                key={character.id} 
                className="character-manager__selected-item"
              >
                <div className="character-manager__selected-info">
                  <div className="character-manager__selected-header">
                    <span className="character-manager__selected-name">{character.name}</span>
                    <span className="character-manager__selected-creature">, {character.creature}</span>
                  </div>
                  <span className="character-manager__selected-description">
                    {character.description}
                  </span>
                </div>
                <Button 
                  variant="text" 
                  onClick={() => handleSelectionChange(character, false)}
                  className="character-manager__remove-button"
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterManager;